// frontend/src/pages/Evidence.js
import React, { useState, useEffect } from 'react';
import axios from '../services/axios';
import toast from 'react-hot-toast';
import {
    PlusIcon,
    DocumentTextIcon,
    PhotoIcon,
    VideoCameraIcon,
    LinkIcon,
} from '@heroicons/react/24/outline';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';
import StatusBadge from '../components/common/StatusBadge';

const Evidence = () => {
    const [loading, setLoading] = useState(true);
    const [evidence, setEvidence] = useState([]);
    const [showUploadForm, setShowUploadForm] = useState(false);
    const [filter, setFilter] = useState('all');

    useEffect(() => {
        loadEvidence();
    }, []);

    const loadEvidence = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/evidence/my-evidence');
            setEvidence(response.data.data);
        } catch (error) {
            toast.error('Failed to load evidence');
        } finally {
            setLoading(false);
        }
    };

    const filteredEvidence = evidence.filter(item => {
        if (filter === 'all') return true;
        if (filter === 'pending') return item.status === 'draft' || item.status === 'submitted';
        if (filter === 'verified') return item.status === 'verified' || item.status === 'approved';
        if (filter === 'rejected') return item.status === 'rejected';
        return true;
    });

    const statusLabels = {
        draft: 'Draft',
        submitted: 'Submitted',
        verified: 'Verified',
        approved: 'Approved',
        rejected: 'Needs Update',
        returned: 'Needs Update'
    };

    const typeIcons = {
        pdf: <DocumentTextIcon className="w-5 h-5" />,
        image: <PhotoIcon className="w-5 h-5" />,
        video: <VideoCameraIcon className="w-5 h-5" />,
        link: <LinkIcon className="w-5 h-5" />,
        document: <DocumentTextIcon className="w-5 h-5" />,
        meeting_notes: <DocumentTextIcon className="w-5 h-5" />,
        reflection: <DocumentTextIcon className="w-5 h-5" />,
        other: <DocumentTextIcon className="w-5 h-5" />
    };

    if (loading) {
        return (
            <div className="space-y-4" role="status" aria-live="polite">
                <div className="skeleton h-10 w-56" />
                <div className="skeleton h-12" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="skeleton h-36" />
                    ))}
                </div>
                <span className="sr-only">Loading evidence…</span>
            </div>
        );
    }

    return (
        <div className="w-full space-y-6">
            <PageHeader
                title="Evidence"
                description="Upload and manage evidence of your work and learning"
                actions={
                    <button
                        type="button"
                        onClick={() => setShowUploadForm(!showUploadForm)}
                        className="btn-primary"
                    >
                        <PlusIcon className="w-5 h-5" aria-hidden="true" />
                        Upload Evidence
                    </button>
                }
            />

            {showUploadForm && (
                <EvidenceUploadForm 
                    onSuccess={() => {
                        setShowUploadForm(false);
                        loadEvidence();
                    }}
                    onCancel={() => setShowUploadForm(false)}
                />
            )}

            <div className="card !py-3 animate-fade-up">
                <div className="flex flex-wrap gap-2">
                    {['all', 'pending', 'verified', 'rejected'].map((filterOption) => (
                        <button
                            key={filterOption}
                            type="button"
                            onClick={() => setFilter(filterOption)}
                            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                                filter === filterOption
                                    ? 'bg-brand-700 text-white'
                                    : 'bg-ink-100 text-ink-600 hover:bg-ink-200'
                            }`}
                        >
                            {filterOption.charAt(0).toUpperCase() + filterOption.slice(1)}
                            {filterOption === 'all' && ` (${evidence.length})`}
                            {filterOption === 'pending' && ` (${evidence.filter(e => e.status === 'draft' || e.status === 'submitted').length})`}
                        </button>
                    ))}
                </div>
            </div>

            {filteredEvidence.length === 0 ? (
                <EmptyState
                    icon={DocumentTextIcon}
                    title="No evidence uploaded"
                    description="Upload evidence of your work to build your professional record."
                    action={
                        <button type="button" onClick={() => setShowUploadForm(true)} className="btn-primary">
                            Upload Evidence
                        </button>
                    }
                />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredEvidence.map((item) => (
                        <EvidenceCard 
                            key={item.id} 
                            evidence={item}
                            onUpdate={loadEvidence}
                            statusLabels={statusLabels}
                            typeIcons={typeIcons}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const EvidenceCard = ({ evidence, onUpdate, statusLabels, typeIcons }) => {
    const handleSubmit = async () => {
        try {
            await axios.post(`/evidence/${evidence.id}/submit`);
            toast.success('Evidence submitted for verification');
            onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to submit');
        }
    };

    return (
        <div className="card-interactive animate-fade-up">
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="text-ink-400 shrink-0">
                            {typeIcons[evidence.evidence_type] || typeIcons.other}
                        </span>
                        <h3 className="font-semibold text-ink-950">{evidence.title}</h3>
                    </div>
                    {evidence.description && (
                        <p className="text-sm text-ink-600 mt-1 line-clamp-2">
                            {evidence.description}
                        </p>
                    )}
                </div>
                <StatusBadge
                    status={evidence.status}
                    label={statusLabels[evidence.status] || evidence.status}
                />
            </div>

            <div className="mt-3 flex flex-wrap gap-2 text-xs text-ink-500">
                {evidence.evidence_type && (
                    <span className="badge badge-neutral">{evidence.evidence_type.replace('_', ' ')}</span>
                )}
                {evidence.file_name && (
                    <span>{evidence.file_name}</span>
                )}
                {evidence.linked_entity_type && (
                    <span>{evidence.linked_entity_type}</span>
                )}
                {evidence.visibility && (
                    <span className="capitalize">{evidence.visibility}</span>
                )}
                <span>{new Date(evidence.created_at).toLocaleDateString()}</span>
            </div>

            {evidence.verification_notes && (
                <div className="mt-2 p-2 bg-ink-50 rounded-lg text-xs text-ink-600 border border-border">
                    <span className="font-semibold">Notes:</span> {evidence.verification_notes}
                </div>
            )}

            <div className="mt-3 flex items-center gap-2">
                {evidence.file_url && (
                    <a 
                        href={evidence.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-semibold text-brand-700 hover:text-brand-800"
                    >
                        View File →
                    </a>
                )}
                {evidence.status === 'draft' && (
                    <button
                        type="button"
                        onClick={handleSubmit}
                        className="btn-primary !min-h-[36px] !py-1.5 text-xs"
                    >
                        Submit for Review
                    </button>
                )}
            </div>
        </div>
    );
};
const EvidenceUploadForm = ({ onSuccess, onCancel }) => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        evidence_type: 'document',
        visibility: 'private',
        file_url: '',
        linked_entity_type: '',
        linked_entity_id: ''
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post('/evidence', formData);
            toast.success('Evidence uploaded successfully');
            onSuccess();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to upload');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="card p-6">
            <h2 className="text-lg font-semibold text-ink-950 mb-4">Upload Evidence</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="label">
                        Title *
                    </label>
                    <input
                        type="text"
                        required
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        className="input-field"
                        placeholder="e.g., Business Assessment Report"
                    />
                </div>

                <div>
                    <label className="label">
                        Description
                    </label>
                    <textarea
                        rows={2}
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        className="input-field"
                        placeholder="Describe your evidence..."
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="label">
                            Type *
                        </label>
                        <select
                            required
                            value={formData.evidence_type}
                            onChange={(e) => setFormData({...formData, evidence_type: e.target.value})}
                            className="input-field"
                        >
                            <option value="document">Document</option>
                            <option value="pdf">PDF</option>
                            <option value="image">Image</option>
                            <option value="video">Video</option>
                            <option value="link">Link</option>
                            <option value="meeting_notes">Meeting Notes</option>
                            <option value="reflection">Reflection</option>
                        </select>
                    </div>
                    <div>
                        <label className="label">
                            Visibility
                        </label>
                        <select
                            value={formData.visibility}
                            onChange={(e) => setFormData({...formData, visibility: e.target.value})}
                            className="input-field"
                        >
                            <option value="private">Private</option>
                            <option value="internal">Internal</option>
                            <option value="public_summary">Public Summary</option>
                            <option value="public_full">Public Full</option>
                        </select>
                    </div>
                </div>

                <div>
                    <label className="label">
                        Upload Attachment File
                    </label>
                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed border-border rounded-xl hover:border-brand-500 transition-colors bg-ink-50/50">
                        <div className="space-y-1 text-center">
                            <DocumentTextIcon className="mx-auto h-10 w-10 text-ink-400" />
                            <div className="flex text-sm text-ink-600 justify-center">
                                <label className="relative cursor-pointer bg-white rounded-md font-medium text-brand-700 hover:text-brand-800 focus-within:outline-none px-2 py-0.5 border border-brand-200">
                                    <span>Upload a file</span>
                                    <input
                                        type="file"
                                        className="sr-only"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                setFormData({
                                                    ...formData,
                                                    file_name: file.name,
                                                    file_url: formData.file_url || `attachment://${file.name}`
                                                });
                                                toast.success(`Attached ${file.name}`);
                                            }
                                        }}
                                    />
                                </label>
                                <p className="pl-1 flex items-center">or drag and drop</p>
                            </div>
                            <p className="text-xs text-ink-500">
                                PDF, DOCX, PNG, JPG up to 10MB
                            </p>
                            {formData.file_name && (
                                <div className="mt-2 text-xs font-semibold text-brand-700 bg-brand-50 inline-block px-3 py-1 rounded-full border border-brand-200">
                                    Attached: {formData.file_name}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div>
                    <label className="label">
                        File URL / External Link (optional)
                    </label>
                    <input
                        type="url"
                        value={formData.file_url.startsWith('attachment://') ? '' : formData.file_url}
                        onChange={(e) => setFormData({...formData, file_url: e.target.value})}
                        className="input-field"
                        placeholder="https://..."
                    />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-border">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="btn-secondary"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading || !formData.title}
                        className="btn-primary"
                    >
                        {loading ? 'Uploading...' : 'Upload Evidence'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default Evidence;
